// viewportPriority — generic IntersectionObserver-driven priority hook.
//
// Three-band priority output:
//   - "visible": element intersects the viewport now → load first
//   - "near":    element is within `rootMargin` of the viewport edge
//                (i.e. the user is likely to scroll into it next) → prefetch
//   - "far":     element is well off-screen → defer entirely
//
// Used by useSnapshotPoll (camera tiles), but the hook itself is
// content-agnostic — wire any RefObject<Element> through it and read
// the returned `priority` to gate work. Live WebRTC sessions on the
// wall would use the exact same hook.
//
// Why two thresholds in one observer:
//   We want "visible" and "near" to be cheap derived signals from the
//   same IO pass. A single observer with `rootMargin` set to the wider
//   "near" band reports `intersectionRatio` per entry, but `rootMargin`
//   doesn't tell us whether the element is in the *real* viewport or
//   only in the expanded band. So we run two observers: one with no
//   margin (visible), one with the prefetch margin (near). Both reuse
//   the same callback since IO callbacks are batched by the browser.

import { useEffect, useRef, useState, type RefObject } from "react";

export type ViewportPriority = "visible" | "near" | "far";

export interface UseViewportPriorityOptions {
  /**
   * Distance from the viewport edge at which an element is considered
   * "near" (about to scroll in). Standard CSS margin syntax. Default:
   * one full viewport above + below ("100% 0px") — generous enough to
   * prefetch the next scroll page without burning bandwidth on the
   * page after that.
   */
  nearRootMargin?: string;
  /**
   * Test seam — inject a custom IntersectionObserver constructor.
   * Defaults to the global one; SSR / jsdom-less envs can pass a stub.
   */
  observerCtor?: typeof IntersectionObserver;
  /**
   * If false, the hook short-circuits to "visible" without observing —
   * useful for tiles whose visibility is a foregone conclusion (e.g.
   * the focused live-view tile on the detail page).
   */
  enabled?: boolean;
}

export interface UseViewportPriorityResult {
  priority: ViewportPriority;
  /**
   * Live ref that callbacks can read without triggering re-renders.
   * Useful for long-lived loops (timers, RTC keepalives) that want
   * the latest priority on each tick without re-subscribing.
   */
  priorityRef: RefObject<ViewportPriority>;
}

/**
 * Observe `targetRef` and report whether it's currently visible, near
 * the viewport, or far enough to skip. Returns both the React-state
 * value (for re-render-driven UI) and a ref (for imperative loops).
 */
export function useViewportPriority(
  targetRef: RefObject<Element | null>,
  opts: UseViewportPriorityOptions = {},
): UseViewportPriorityResult {
  const { nearRootMargin = "100% 0px", observerCtor, enabled = true } = opts;

  const [priority, setPriority] = useState<ViewportPriority>(
    enabled ? "far" : "visible",
  );
  const priorityRef = useRef<ViewportPriority>(
    enabled ? "far" : "visible",
  );

  useEffect(() => {
    priorityRef.current = priority;
  }, [priority]);

  useEffect(() => {
    if (!enabled) {
      setPriority("visible");
      priorityRef.current = "visible";
      return undefined;
    }
    const target = targetRef.current;
    if (target === null) return undefined;

    const Ctor =
      observerCtor ??
      (typeof IntersectionObserver !== "undefined"
        ? IntersectionObserver
        : null);
    if (Ctor === null) {
      // No IO support (very old browser / jsdom) — fall back to
      // "visible" so the page still works, just without prioritization.
      setPriority("visible");
      priorityRef.current = "visible";
      return undefined;
    }

    let visible = false;
    let near = false;

    function update(): void {
      const next: ViewportPriority = visible
        ? "visible"
        : near
          ? "near"
          : "far";
      if (next !== priorityRef.current) {
        priorityRef.current = next;
        setPriority(next);
      }
    }

    const visibleObs = new Ctor(
      (entries) => {
        for (const e of entries) {
          if (e.target === target) {
            visible = e.isIntersecting;
          }
        }
        update();
      },
      { root: null, rootMargin: "0px", threshold: 0 },
    );
    const nearObs = new Ctor(
      (entries) => {
        for (const e of entries) {
          if (e.target === target) {
            near = e.isIntersecting;
          }
        }
        update();
      },
      { root: null, rootMargin: nearRootMargin, threshold: 0 },
    );

    visibleObs.observe(target);
    nearObs.observe(target);

    return () => {
      visibleObs.disconnect();
      nearObs.disconnect();
    };
  }, [targetRef, nearRootMargin, observerCtor, enabled]);

  return { priority, priorityRef };
}
